<script setup>
import {Dialog, DialogOverlay, DialogPanel, TransitionChild, TransitionRoot} from '@headlessui/vue';
import {Icon} from "@iconify/vue";
import Button from "@/components/Button/index.vue";
import {useChatStore} from "@/stores/chat.js";
import {nextTick, onMounted, ref} from "vue";
import {useUploadStore} from "@/stores/upload.js";
import {useUserStore} from "@/stores/user.js";

const uploadStore = useUploadStore();
const chatStore = useChatStore();
const userStore = useUserStore()
const fileInput = ref(null);
const sendingFileStatus = ref('');
const sendButton = ref(null);
const emit = defineEmits(['scrollToBottom'])

const handlePaste = (event) => {
  if (!chatStore.activeChat) return;

  const items = (event.clipboardData || event.originalEvent.clipboardData).items;
  for (const item of items) {
    if (item.kind === "file") {
      const file = item.getAsFile();
      chatStore.selectedFile = {
        file,
        url: URL.createObjectURL(file),
      };
      chatStore.activeFilePreview = true;
      break;
    }
  }
};

const resetFile = () => {
  chatStore.selectedFile = {};
  chatStore.activeFilePreview = false;
  if (fileInput.value) {
    fileInput.value.value = '';
  }
};

const sendFile = async () => {
  if (sendingFileStatus.value === 'uploading') return; // جلوگیری از ارسال مجدد
  const file = chatStore.selectedFile.file
  const extension = file.name.split('.').pop()
  const baseName = file.name.split('.').slice(0, -1).join('.')
  const uniqueName = `${baseName}-${file.lastModified}.${extension}`
  resetFile();

  const thisChannel = chatStore.selectedChat.channel
  const thisStore = chatStore.selectedChat.store
  const tempMessage = {
    id: Date.now() + Math.random(),
    type: 'file',
    from: chatStore.chatData.store.split(' USD')[0] + " - " + userStore.getName, // یا userStore.getName
    time: new Date(),
    file: {
      url: URL.createObjectURL(file),
      type: file.type,
      name: uniqueName
    },
    pending: true,
    failed: false
  };
  chatStore.addPendingMessage1(tempMessage);
  setTimeout(async () => {
    await nextTick();
    emit('scrollToBottom')
  }, 200)
  sendingFileStatus.value = 'uploading';
  try {
    const result = await uploadStore.uploadFile({
      fileName: uniqueName,
      file: file
    }, {returnResponse: true});

    const isUploaded = result?.success

    if (isUploaded) {
      await chatStore.action_send_file({
        channel: thisChannel,
        store: thisStore,
        fileName: result.data.file.name
      });
      // حذف از pending بعد از ارسال واقعی
      //chatStore.removePendingMessage(tempMessage.id);
    } else {
      tempMessage.pending = false;
      tempMessage.failed = true;
    }
  } catch (err) {
    tempMessage.pending = false;
    tempMessage.failed = true;
  } finally {
    sendingFileStatus.value = '';
  }
};

onMounted(() => {
  window.addEventListener("paste", handlePaste);
});

</script>

<template>
  <TransitionRoot appear :show="chatStore.activeFilePreview && chatStore.activeChat" as="template">
    <Dialog as="div" @close="resetFile" class="relative z-[9999]" :initial-focus="sendButton">
      <TransitionChild
          as="template"
          enter="duration-300 ease-out"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="duration-200 ease-in"
          leave-from="opacity-100"
          leave-to="opacity-0"
      >
        <DialogOverlay class="fixed inset-0 bg-[black]/60"/>
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex h-full overflow-hidden items-center justify-center px-4 py-8">
          <TransitionChild
              as="template"
              enter="duration-300 ease-out"
              enter-from="opacity-0 scale-95"
              enter-to="opacity-100 scale-100"
              leave="duration-200 ease-in"
              leave-from="opacity-100 scale-100"
              leave-to="opacity-0 scale-95"
          >
            <DialogPanel
                class="flex flex-col overflow-hidden rounded-md w-fit xl:max-w-xl lg:max-w-lg max-h-[50%] text-left align-middle shadow-xl transition-all bg-white dark:bg-black-800 text-black-600 dark:text-white">
              <div class="flex-none flex flex-row justify-between px-4 border-b border-slate-700">
                <div class="text-base py-3">
                  Confirm File Upload
                </div>
                <div class="flex flex-row items-center gap-4">
                  <button
                      type="button"
                      class="text-white-dark outline-none hover:text-red-500"
                      @click="resetFile"
                  >
                    <Icon icon="pajamas:close-xs"/>
                  </button>
                </div>
              </div>
              <div class="flex-1 flex p-5 overflow-hidden h-full gap-6 justify-center size-60">
                <div v-if="chatStore.selectedFile.url" class="flex h-auto text-center justify-center">
                  <img :src="chatStore.selectedFile.url" class="rounded-lg" alt=""/>
                </div>
              </div>
              <div
                  class="flex-none flex flex-row justify-center align-center w-full px-4 pb-4 space-x-4 rtl:space-x-reverse">
                <Button
                    text="Close"
                    btnClass="btn-outline-dark"
                    @click="resetFile"
                />
                <button
                    ref="sendButton"
                    type="submit"
                    class="btn btn-outline-primary text-center flex items-center justify-center gap-3"
                    @click="sendFile"
                >
                  Send
                  <Icon v-show="sendingFileStatus === 'uploading'" icon="eos-icons:bubble-loading" height="24"/>
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<style scoped lang="scss">
</style>
